import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { Plus, CalendarDays, Building2, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCalendar() {
  const [visits, setVisits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    company_id: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [visitsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/visits`),
        axios.get(`${API}/companies`)
      ]);
      setVisits(visitsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id) {
      toast.error('Selecciona una empresa');
      return;
    }

    try {
      await axios.post(`${API}/visits`, formData);
      toast.success('Visita programada');
      setDialogOpen(false);
      setFormData({ company_id: '', scheduled_date: format(new Date(), 'yyyy-MM-dd'), notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al programar visita');
    }
  };

  const handleUpdateStatus = async (visitId, status) => {
    try {
      await axios.put(`${API}/visits/${visitId}?status=${status}`);
      toast.success('Visita actualizada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta visita?')) return;
    try {
      await axios.delete(`${API}/visits/${id}`);
      toast.success('Visita eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      programada: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      completada: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      cancelada: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    const labels = { programada: 'Programada', completada: 'Completada', cancelada: 'Cancelada' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-mono uppercase tracking-wider border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  // Get visits for a specific date
  const getVisitsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return visits.filter(v => v.scheduled_date === dateStr);
  };

  // Days with visits for calendar highlighting
  const daysWithVisits = visits.map(v => parseISO(v.scheduled_date));

  const selectedDateVisits = getVisitsForDate(selectedDate);

  const upcomingVisits = useMemo(() => visits.filter(v => v.status === 'programada'), [visits]);

  return (
    <Layout title="Calendario de Visitas">
      <div className="space-y-6 animate-fade-in" data-testid="admin-calendar">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Programa y gestiona las visitas a tus clientes</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="add-visit-btn">
                <Plus className="w-4 h-4" />
                Nueva Visita
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Programar Visita</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Empresa *</Label>
                  <Select
                    value={formData.company_id || undefined}
                    onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="visit-company-select">
                      <SelectValue placeholder="Selecciona empresa" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Fecha *</Label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="visit-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Notas</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
                    placeholder="Notas adicionales para la visita..."
                    data-testid="visit-notes-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="save-visit-btn">
                    Programar Visita
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="bg-slate-900/50 border-slate-800 lg:col-span-1">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="font-rajdhani text-lg text-white">Selecciona una fecha</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={es}
                className="rounded-md"
                modifiers={{
                  hasVisit: daysWithVisits
                }}
                modifiersStyles={{
                  hasVisit: { 
                    backgroundColor: 'rgba(14, 165, 233, 0.2)',
                    borderRadius: '4px'
                  }
                }}
              />
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                <div className="w-4 h-4 rounded bg-sky-500/20" />
                <span>Días con visitas programadas</span>
              </div>
            </CardContent>
          </Card>

          {/* Visits for selected date */}
          <Card className="bg-slate-900/50 border-slate-800 lg:col-span-2">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="font-rajdhani text-lg text-white flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-sky-400" />
                Visitas para {format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: es })}
                {isToday(selectedDate) && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-mono uppercase tracking-wider bg-sky-500/20 text-sky-400 ml-2">
                    Hoy
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Cargando...</div>
              ) : selectedDateVisits.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay visitas programadas para este día</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDateVisits.map((visit) => (
                    <div key={visit.id} className="p-4 bg-slate-800/50 rounded-sm border border-slate-700">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-4 h-4 text-slate-500" />
                            <span className="font-medium text-white">{visit.company_name}</span>
                            {getStatusBadge(visit.status)}
                          </div>
                          {visit.notes && (
                            <p className="text-sm text-slate-400 mt-2">{visit.notes}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {visit.status === 'programada' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleUpdateStatus(visit.id, 'completada')}
                                className="text-slate-400 hover:text-emerald-400"
                                title="Marcar como completada"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleUpdateStatus(visit.id, 'cancelada')}
                                className="text-slate-400 hover:text-rose-400"
                                title="Cancelar visita"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(visit.id)}
                            className="text-slate-400 hover:text-rose-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming visits list */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="font-rajdhani text-lg text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Próximas Visitas Programadas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {upcomingVisits.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No hay visitas programadas</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {upcomingVisits
                  .slice(0, 10)
                  .map((visit) => (
                    <div key={visit.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-rajdhani font-bold text-white">
                              {format(parseISO(visit.scheduled_date), 'd')}
                            </p>
                            <p className="text-xs text-slate-500 uppercase">
                              {format(parseISO(visit.scheduled_date), 'MMM', { locale: es })}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-white">{visit.company_name}</p>
                            <p className="text-sm text-slate-400">
                              {format(parseISO(visit.scheduled_date), 'EEEE', { locale: es })}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(visit.status)}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
